import React from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SERVICES } from "../mock";

const PRODUCT_DATA = {
  "tatu-disk-harrows": {
    title: "TATU Disk Harrows",
    eyebrow: "AGRICULTURAL EQUIPMENT",
    blurb: "Heavy-duty disk harrows engineered by TATU Marchesan — the global standard for tillage in citrus, sugar cane, and row crop operations.",
    img: "https://images.unsplash.com/photo-1527847263472-aa5338d178b8?w=1400&q=85",
    features: [
      "22\" to 36\" disk diameters available",
      "Hydraulic and mechanical configurations",
      "Hardened steel bearings, sealed for life",
      "Ships fully assembled, ready to hitch",
    ],
  },
  "tatu-assorted-parts": {
    title: "TATU Assorted Parts",
    eyebrow: "GENUINE PARTS",
    blurb: "Hard-to-find accessories and consumables for the TATU Marchesan line — in stock, ready to ship.",
    img: "https://images.pexels.com/photos/12982187/pexels-photo-12982187.jpeg?w=1400",
    features: ["Bolts, washers, retainers", "Hubs, scrapers, spools", "Hydraulic cylinders & seals", "Same-day shipping on most SKUs"],
  },
  "tatu-replacement-parts": {
    title: "TATU Replacement Parts",
    eyebrow: "OEM REPLACEMENT",
    blurb: "Direct-fit replacement components for every TATU harrow we sell. Keep your equipment in the field.",
    img: "https://images.unsplash.com/photo-1594771804886-a933bb2d609b?w=1400&q=85",
    features: ["Disk blades — plain & notched", "Bearings, hubs, axles", "Frame & hitch components", "Genuine TATU OEM — not aftermarket"],
  },
  "plasma-cutting": {
    title: "Plasma Cutting Services",
    eyebrow: "CUSTOM CNC",
    blurb: "Send us a DXF — we’ll cut, deburr, and ship. High-precision plasma on steel and aluminum up to 1\" thick.",
    img: "https://images.unsplash.com/photo-1698664683348-f9f35b809821?w=1400&q=85",
    features: ["DXF, DWG, STEP files accepted", "Mild steel, stainless, aluminum", "Production runs or one-offs", "Optional powder coating & finishing"],
  },
  "agricultural-equipment": {
    title: "Agricultural Equipment",
    eyebrow: "FIELD READY",
    blurb: "Tillage, hauling, and storage equipment for the modern farm — in stock and ready to work.",
    img: "https://images.unsplash.com/photo-1615811361523-6bd03d7748e7?w=1400&q=85",
    features: ["TATU disk harrows & plows", "Custom implement modifications", "On-site equipment service", "Financing & lease options"],
  },
  enclosures: {
    title: "Custom Enclosures",
    eyebrow: "FABRICATION",
    blurb: "NEMA-rated steel and aluminum enclosures — sized to your generator, control panel, or pump set.",
    img: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1400&q=85",
    features: ["NEMA 3R / 4 / 4X options", "Custom cutouts & hardware", "Powder coated to spec", "UL-listed available"],
  },
  "fuel-tanks": {
    title: "Fuel Tanks",
    eyebrow: "STORAGE",
    blurb: "UL-142 listed above-ground fuel storage — single & double wall, single product or split.",
    img: "https://images.pexels.com/photos/22717514/pexels-photo-22717514.jpeg?w=1400",
    features: ["250 – 20,000 gallon capacity", "UL-142 / UL-2085 listed", "Diesel, gasoline, biodiesel", "Complete piping packages"],
  },
  trailers: {
    title: "Custom Trailers",
    eyebrow: "HAUL",
    blurb: "Heavy-duty utility and equipment trailers — fabricated to your spec, road-legal and built to last.",
    img: "https://images.unsplash.com/photo-1594771804886-a933bb2d609b?w=1400&q=85",
    features: ["7K – 30K GVWR ranges", "Tandem, tri-axle, gooseneck", "DOT-compliant lighting & brakes", "Custom decks & ramps"],
  },
  gauges: { title: "Gauges, Senders & Switches", eyebrow: "TANK PARTS", blurb: "Accurate level monitoring components for above and below-ground fuel storage.", img: "https://images.pexels.com/photos/13296053/pexels-photo-13296053.jpeg?w=1400", features: ["Mechanical & electronic gauges", "Resistive & capacitive senders", "High-level alarm switches", "Compatible with all major brands"] },
  "caps-adaptors": { title: "Caps & Adaptors", eyebrow: "TANK PARTS", blurb: "Vapor-tight caps and fill adaptors from trusted industry manufacturers.", img: "https://images.unsplash.com/photo-1698664683348-f9f35b809821?w=1400&q=85", features: ["Tight-fill & loose-fill", "OPW & EBW compatible", "Lockable variants in stock", "Replacement gaskets available"] },
  "spill-containers": { title: "Spill Containers", eyebrow: "TANK PARTS", blurb: "5 to 15-gallon spill buckets engineered to keep fuel out of the ground.", img: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1400&q=85", features: ["5, 7.5, 15 gal capacities", "Drain valves — manual or auto", "Cast iron or polymer", "EPA SPCC compliant"] },
  vents: { title: "Tank Vents", eyebrow: "TANK PARTS", blurb: "Emergency, pressure, and atmospheric vents for safe tank operation.", img: "https://images.pexels.com/photos/12982187/pexels-photo-12982187.jpeg?w=1400", features: ["Atmospheric & emergency styles", "2\", 3\", 4\" inlet sizes", "UL-listed flame arresters", "Storm-cap variants available"] },
};

export default function ProductPage() {
  const { slug } = useParams();
  const data = PRODUCT_DATA[slug] || {
    title: "Product",
    eyebrow: "EMC INDUSTRIAL",
    blurb: "This page is being updated. Call us for current specs and pricing.",
    img: "https://images.unsplash.com/photo-1615811361523-6bd03d7748e7?w=1400&q=85",
    features: [],
  };

  return (
    <>
      <Header />

      <section className="relative h-[55vh] min-h-[400px] bg-[#0a0a0a] flex items-center">
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: `linear-gradient(rgba(10,10,10,0.5), rgba(10,10,10,0.4)), url('${data.img}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-7xl mx-auto px-6">
          <span className="inline-block bg-[#f5a623] text-[#0a0a0a] px-3 py-1.5 text-[11px] font-bold tracking-[0.18em] mb-5">{data.eyebrow}</span>
          <h1 className="display-font text-5xl md:text-7xl font-bold text-white max-w-3xl leading-[0.95]">{data.title}</h1>
          <p className="text-white/85 text-lg max-w-2xl mt-5">{data.blurb}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <Reveal>
            <h2 className="display-font text-3xl md:text-4xl font-bold mb-6">Specs & Features</h2>
            <ul className="space-y-4">
              {data.features.map((f) => (
                <li key={f} className="flex gap-3 items-start text-[#222] text-lg">
                  <CheckCircle2 className="w-5 h-5 mt-1 text-[#f5a623] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-[#fafafa] border border-[#e6e6e6] p-8">
              <h3 className="display-font text-2xl font-bold mb-3">Need a custom configuration?</h3>
              <p className="text-[#555] mb-5">
                Every job we run is quoted within one business day. Call, email, or drop in —
                we’ll talk specs and timing.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#contact" className="btn-amber px-6 py-3 font-bold uppercase tracking-wider text-sm">Request Quote</a>
                <Link to="/" className="inline-flex items-center gap-2 border-2 border-[#0a0a0a] text-[#0a0a0a] px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#0a0a0a] hover:text-white transition-colors">
                  Back Home <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {SERVICES.slice(0, 3).map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.title} className="bg-white border border-[#e6e6e6] p-4 text-center">
                    <Icon className="w-5 h-5 text-[#f5a623] mx-auto mb-2" />
                    <div className="text-xs font-semibold">{s.title}</div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </>
  );
}
